'use client';

import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import type { WorldCupMatchHeaderData } from '@/components/predic/types/market';
import { useTranslation } from '@/lib/i18n';
import { Skeleton } from '@mui/material';
import { useHeader } from '@/contexts/HeaderContext';
import { AudioLines } from 'lucide-react';
import { cn } from '@/lib/utils';
import HoldingPositions from './detail/market/HoldingPositions';
import { PolymarketMarket } from './detail/market/types';
import useMarketEvent from './hooks/use-market-event';
import useWorldCupEvent, { isLiveStatus, WorldCupEventDetail } from './hooks/use-world-cup-event';
import FixedChatWindow from './message/FixedChatWindow';
import PythraSummary from './ui/PythraSummary';
import GamePriceTrend from './world-cup/GamePriceTrend';
import WorldCupDataTabs from './world-cup/WorldCupDataTabs';
import WorldCupMatchGroup from './world-cup/WorldCupMatchGroup';
import WorldCupMatchHeader from './world-cup/WorldCupMatchHeader';
import { translateWorldCupTeamName, translateWorldCupTeamText } from './world-cup/teamTranslation';
import MarketHeader from './detail/market/MarketHeader';
import { formatDateTime } from '@/lib/utils/date';

interface WorldCupPageProps {
  eventSlug: string;
  openInSidebar?: boolean;
}
export default function WorldCupPage({ eventSlug, openInSidebar }: WorldCupPageProps) {
  const { t } = useTranslation();
  const { setShowBackButton } = useHeader();
  const { eventData, event, markets, isLoading } = useMarketEvent({
    eventSlug,
    enabled: !!eventSlug,
  });
  const { data } = useWorldCupEvent({ eventSlug });
  const headerData = useMemo(() => (data ? buildMatchHeaderData(data) : null), [data]);
  const trendingCard = useMemo(
    () => (data ? buildTrendingCard(data, markets, eventData?.event?.total_volume) : null),
    [data, markets, eventData?.event?.total_volume],
  );
  const isClosed = event?.closed === 1;
  const questions = useMemo(() => {
    const homeTeam = data?.home_team?.name;
    const awayTeam = data?.away_team?.name;
    if (homeTeam && awayTeam) {
      return [
        t(`Upcoming matches for {{team}}?`, { team: translateWorldCupTeamName(homeTeam, t) }),
        t(`Upcoming matches for {{team}}?`, { team: translateWorldCupTeamName(awayTeam, t) }),
      ];
    }
    return null;
  }, [data?.away_team?.name, data?.home_team?.name, t]);

  console.log(trendingCard);

  useEffect(() => {
    setShowBackButton(true);
    // setIsTransparent(false);
    return () => setShowBackButton(false);
  }, [setShowBackButton]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="rounded" width="100%" height={60} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" width="100%" height={200} sx={{ bgcolor: 'grey.800' }} />
        <Skeleton variant="rounded" width="100%" height={260} sx={{ bgcolor: 'grey.800' }} />
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-245 pb-25 space-y-4 text-sm">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'flex items-center gap-1 rounded-full text-[#14C784] bg-[#14C784]/20 px-2',
              { 'bg-[#2D2D2D] text-[#B7B9BC]': isFinalStatus(data?.score?.status) },
            )}
          >
            {isLiveStatus(data?.score?.status) && <AudioLines size={14} />}
            <span className="capitalize font-medium">
              {data?.score?.status === 'STATUS_SCHEDULED' && t('Upcoming')}
              {isLiveStatus(data?.score?.status) && t('Live')}
              {isFinalStatus(data?.score?.status) && t('Final')}
            </span>
          </div>
          <span className="capitalize text-[#757575]">{t('world cup')}</span>
          <span className="size-1 rounded-full mx-1 bg-[#757575]" />
          <span className="capitalize text-[#757575]">
            {t(`Group {{team}}`, { team: normalizeGroupLabel(data?.group) })}
          </span>
        </div>
        <MarketHeader
          title={data?.title ? translateWorldCupTeamText(data.title, t) : t('World Cup Match')}
          marketEventId={data?.polymarket_id || ''}
          polymarketUrl={data?.source || ''}
        />
        {headerData && <WorldCupMatchHeader {...headerData} />}
        <GamePriceTrend markets={markets} openInSidebar={openInSidebar} />
        {event?.summary && <PythraSummary summary={event.summary} />}
        <WorldCupDataTabs eventSlug={eventSlug} />
        <WorldCupMatchGroup eventSlug={eventSlug} />
        {event?.id && <HoldingPositions eventId={event.id} markets={markets} isClosed={isClosed} />}
      </div>
      {!openInSidebar && (
        <FixedChatWindow
          suggestions={questions || event?.recommended_questions || []}
          sourceId={`market_${event?.slug}`}
          userContext={
            trendingCard
              ? JSON.stringify({
                  context_type: 'world_cup',
                  data: {
                    ...eventData,
                    trending_card: trendingCard,
                  },
                })
              : undefined
          }
        />
      )}
    </>
  );
}

function isFinalStatus(status?: string | null) {
  return status && ['FINAL', 'STATUS_FINAL', 'STATUS_FULL_TIME'].includes(status);
}

function buildMatchHeaderData(event: WorldCupEventDetail): WorldCupMatchHeaderData {
  return {
    home_team: {
      name: event.home_team.name,
      logo_src: event.home_team.logo ?? undefined,
      avatar_fallback: getInitials(event.home_team.name),
    },
    away_team: {
      name: event.away_team.name,
      logo_src: event.away_team.logo ?? undefined,
      avatar_fallback: getInitials(event.away_team.name),
    },
    score: formatScore(event),
    match_date: formatMatchDate(event),
  };
}

function formatScore(event: WorldCupEventDetail) {
  if (!event.score || event.score.status === 'STATUS_SCHEDULED') {
    return formatMatchTime(event.game_start_time) || 'vs';
  }

  return `${event.score.home} - ${event.score.away}`;
}

function formatMatchTime(dateValue?: string | null) {
  if (!dateValue) return '';

  const date = dayjs(dateValue);
  if (!date.isValid()) return '';

  return formatDateTime(date, 'hh:mm A');
}

function formatMatchDate(event: WorldCupEventDetail) {
  const dateValue = event.game_start_time || event.end_date;
  if (event.score && isLiveStatus(event.score.status)) {
    if (event.score.status === 'STATUS_FIRST_HALF') {
      return `1H - ${event.score.status_detail?.replace("'", '')}`;
    }
    if (event.score.status === 'STATUS_SECOND_HALF') {
      return `2H - ${event.score.status_detail?.replace("'", '')}`;
    }
    if (event.score.status === 'STATUS_HALFTIME') {
      return 'HT';
    }
  }

  if (event?.score?.status === 'STATUS_FULL_TIME') {
    return 'FT';
  }

  if (event?.score?.status === 'STATUS_FIRST_HALF') {
    return event?.score?.status_detail?.replace("'", '') || '';
  }

  if (!dateValue) return '';

  const date = dayjs(dateValue);
  if (!date.isValid()) return '';

  return formatDateTime(date, 'MMMM D');
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

interface TrendingCardTeam {
  name: string;
  logo: string;
  yesPrice: string;
  ordering: 'home' | 'away';
}

interface TrendingCardData {
  eventId: string;
  eventSlug: string;
  title: string;
  draw: string;
  totalVolume: string;
  type: string;
  teams: TrendingCardTeam[];
}

function buildTrendingCard(
  event: WorldCupEventDetail,
  markets: PolymarketMarket[] | undefined,
  totalVolume: string | undefined,
): TrendingCardData {
  const priceMap = new Map<string, string>();
  for (const market of markets ?? []) {
    if (market.condition_id) {
      priceMap.set(market.condition_id, market.outcome_yes_price ?? '');
    }
    if (market.market_id) {
      priceMap.set(String(market.market_id), market.outcome_yes_price ?? '');
    }
  }

  const getMarketPrice = (market: PolymarketMarket) => {
    return (
      priceMap.get(market.condition_id) ??
      priceMap.get(String(market.market_id)) ??
      market.outcome_yes_price ??
      ''
    );
  };

  const findMarketPrice = (label: string) => {
    const normalizedLabel = normalizeMarketLabel(label);
    const market = markets?.find((m) => {
      const normalizedGroupTitle = normalizeMarketLabel(m.group_item_title);
      const normalizedOutcomeYes = normalizeMarketLabel(m.outcome_yes);

      return (
        normalizedGroupTitle === normalizedLabel ||
        normalizedOutcomeYes === normalizedLabel ||
        normalizeMarketLabel(stripMarketLabelContext(m.group_item_title)) === normalizedLabel ||
        normalizeMarketLabel(stripMarketLabelContext(m.outcome_yes)) === normalizedLabel
      );
    });

    if (!market) return '';
    return getMarketPrice(market);
  };

  const drawMarket = markets?.find((market) => {
    return ['group_item_title', 'outcome_yes'].some((key) => {
      const value = market[key as keyof PolymarketMarket];
      return normalizeMarketLabel(stripMarketLabelContext(String(value ?? ''))) === 'draw';
    });
  });
  const drawPrice = drawMarket ? getMarketPrice(drawMarket) : findMarketPrice('Draw');

  const trendingCard: TrendingCardData = {
    title: event.title,
    eventId: String(event.polymarket_id),
    eventSlug: event.slug,
    draw: drawPrice,
    totalVolume: String(totalVolume ?? '0'),
    type: 'world-cup-match',
    teams: [
      {
        name: event.home_team.abbr || event.home_team.name,
        logo: event.home_team.logo ?? '',
        yesPrice: findMarketPrice(event.home_team.name),
        ordering: 'home',
      },
      {
        name: event.away_team.abbr || event.away_team.name,
        logo: event.away_team.logo ?? '',
        yesPrice: findMarketPrice(event.away_team.name),
        ordering: 'away',
      },
    ],
  };

  return trendingCard;
}

function normalizeMarketLabel(value?: string | null) {
  return (value ?? '').trim().toLowerCase();
}

function stripMarketLabelContext(value?: string | null) {
  return (value ?? '').replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function normalizeGroupLabel(value?: string | null) {
  return (value ?? '').replace(/^group\s+/i, '').trim();
}
